// src/features/payment/NewPaymentModal.tsx
// Charge or authorize a new payment. Covers ChargePayment and AuthorizePayment —
// same request shape, different intent ("charge now" vs "hold funds").

import { useEffect, useState } from 'react';
import { useI18n } from '@react/hooks';
import { FormDialog, FormRow } from '@/components/form';
import { errMessage } from './format';

export interface NewPaymentValue {
  billing_account_id: string;
  payment_method_id: string;
  amount: string;
  currency: string;
  description: string;
}

export interface NewPaymentModalProps {
  open: boolean;
  mode: 'charge' | 'authorize';
  isSaving?: boolean;
  onCancel: () => void;
  onSubmit: (value: NewPaymentValue) => Promise<void>;
}

const emptyForm = (): NewPaymentValue => ({ billing_account_id: '', payment_method_id: '', amount: '', currency: 'USD', description: '' });

export function NewPaymentModal({ open, mode, isSaving, onCancel, onSubmit }: NewPaymentModalProps) {
  const { t } = useI18n();
  const [form, setForm] = useState<NewPaymentValue>(emptyForm());
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setForm(emptyForm());
    setError(null);
  }, [open]);

  const change = (field: keyof NewPaymentValue, value: string) => setForm((p) => ({ ...p, [field]: value }));

  const canSubmit = !!form.billing_account_id.trim() && !!form.payment_method_id.trim() && !!form.amount.trim() && !!form.currency.trim();
  const dirty = !!(form.billing_account_id.trim() || form.payment_method_id.trim() || form.amount.trim() || form.description.trim());

  const submit = async () => {
    if (!canSubmit || isSaving) return;
    setError(null);
    try {
      await onSubmit(form);
    } catch (err) {
      setError(errMessage(err, t('payment.ledger.createError')));
    }
  };

  return (
    <FormDialog
      open={open}
      onClose={onCancel}
      onSubmit={submit}
      submitLabel={mode === 'charge' ? t('payment.ledger.charge') : t('payment.ledger.authorize')}
      canSubmit={canSubmit}
      isSubmitting={isSaving}
      dirty={dirty}
      aria-label={mode === 'charge' ? t('payment.ledger.newCharge') : t('payment.ledger.newAuthorize')}
    >
      <div className="space-y-1 px-3.5 pb-4 pt-3">
        {error && <p className="mb-2 text-sm text-destructive">{error}</p>}

        <FormRow label={t('payment.ledger.billingAccountId')} labelWidth="sm">
          <input
            data-autofocus
            value={form.billing_account_id}
            onChange={(e) => change('billing_account_id', e.target.value)}
            autoCapitalize="off"
            className="w-full rounded-md border border-input bg-background px-2.5 py-1 font-mono text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
          />
        </FormRow>
        <FormRow label={t('payment.ledger.paymentMethodId')} labelWidth="sm">
          <input
            value={form.payment_method_id}
            onChange={(e) => change('payment_method_id', e.target.value)}
            autoCapitalize="off"
            className="w-full rounded-md border border-input bg-background px-2.5 py-1 font-mono text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
          />
        </FormRow>
        <FormRow label={t('payment.ledger.amount')} labelWidth="sm">
          <input
            value={form.amount}
            onChange={(e) => change('amount', e.target.value)}
            inputMode="decimal"
            placeholder="0.00"
            className="w-full rounded-md border border-input bg-background px-2.5 py-1 text-right text-sm tabular-nums text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
          />
        </FormRow>
        <FormRow label={t('payment.ledger.currency')} labelWidth="sm">
          <input
            value={form.currency}
            onChange={(e) => change('currency', e.target.value.toUpperCase())}
            placeholder="USD"
            autoCapitalize="off"
            className="w-full rounded-md border border-input bg-background px-2.5 py-1 font-mono text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
          />
        </FormRow>
        <FormRow label={t('common.description')} labelWidth="sm">
          <input
            value={form.description}
            onChange={(e) => change('description', e.target.value)}
            autoCapitalize="off"
            className="w-full rounded-md border border-input bg-background px-2.5 py-1 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
          />
        </FormRow>
      </div>
    </FormDialog>
  );
}

export default NewPaymentModal;
